import { useRef } from 'react';
import { ErrorText } from '../ErrorText';
import { formatCurrencyWhole } from '../../utils/formatCurrency';
import {
  Chip,
  ChipPrice,
  ChipTime,
  ChipsRow,
  ErrorRow,
  Header,
  NavButton,
  NavControls,
  Title,
  Wrapper,
} from './TimeslotChipSelector.styles';
import type { TimeslotChipSelectorProps } from './TimeslotChipSelector.types';

export function TimeslotChipSelector({
  timeslots,
  selectedTimeslot,
  onTimeslotSelect,
  wrap = false,
  labels,
  hasError,
  errorText,
}: TimeslotChipSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const title = labels?.title ?? 'Time';
  const prevLabel = labels?.previousPage ?? 'Previous timeslots';
  const nextLabel = labels?.nextPage ?? 'Next timeslots';
  const locale = labels?.locale ?? 'en-GB';

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -amount : amount });
  };

  return (
    <Wrapper>
      {!wrap && (
        <Header>
          <Title>{title}</Title>
          <NavControls>
            <NavButton type="button" aria-label={prevLabel} onClick={() => scroll('left')}>
              &#8249;
            </NavButton>
            <NavButton type="button" aria-label={nextLabel} onClick={() => scroll('right')}>
              &#8250;
            </NavButton>
          </NavControls>
        </Header>
      )}
      <ChipsRow
        ref={scrollRef}
        role="listbox"
        aria-label={title}
        data-wrap={wrap || undefined}
        aria-invalid={hasError || undefined}
        tabIndex={hasError ? -1 : undefined}
      >
        {timeslots.map((slot) => {
          const isSelected = slot.value === selectedTimeslot;
          return (
            <Chip
              key={slot.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              {...(isSelected ? { 'data-selected': '' } : undefined)}
              onClick={() => onTimeslotSelect(slot.value)}
            >
              <ChipTime $selected={isSelected}>{slot.label}</ChipTime>
              {slot.price != null && slot.currencyCode && (
                <ChipPrice $selected={isSelected}>
                  {formatCurrencyWhole(slot.price, slot.currencyCode, locale)}
                </ChipPrice>
              )}
            </Chip>
          );
        })}
      </ChipsRow>
      {hasError && errorText && (
        <ErrorRow>
          <ErrorText>{errorText}</ErrorText>
        </ErrorRow>
      )}
    </Wrapper>
  );
}
